import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Shield, Users, Check, X, AlertCircle, Info, Plus, Pencil, Trash2, Tag, ChevronRight } from 'lucide-react';

// Available RBAC permissions with descriptions
// Note: Platform-level billing permissions (refunds, invoice void, billing audit, direct bill approve)
// are managed in App Admin Permissions, not here.
const RBAC_PERMISSIONS = [
  { value: 'org_admin', label: 'Organization Admin', description: 'Full access to all organization features' },
  { value: 'teams', label: 'Teams', description: 'Create and manage teams' },
  { value: 'user_assignments', label: 'User Assignments', description: 'Assign executives to users' },
  { value: 'manager_relationships', label: 'Manager Relationships', description: 'Manage user reporting structure' },
  { value: 'audit_logs', label: 'Audit Logs', description: 'View audit logs' },
  { value: 'audit_logs_configure', label: 'Audit Log Configuration', description: 'Configure S3 export settings' },
  { value: 'users_invites', label: 'Users & Invites', description: 'Manage users and send invites' },
  { value: 'saml_configure', label: 'SAML Configuration', description: 'Configure SAML 2.0 SSO settings' },
  { value: 'oidc_configure', label: 'OIDC Configuration', description: 'Configure OIDC SSO settings' },
  // Organization-level billing permissions
  { value: 'billing_view', label: 'Billing View', description: 'View organization invoices, payments, and billing profile' },
  { value: 'billing_manage', label: 'Billing Manage', description: 'Manage organization payment methods and billing profile' },
  { value: 'billing_payments', label: 'Billing Payments', description: 'Initiate payments for the organization' },
  { value: 'billing_subscription_approve', label: 'Subscription Approve', description: 'Approve subscription proposals for the organization' },
  // Integration permissions
  { value: 'urbanride_access', label: 'Urbanride Access', description: 'Access Urbanride API keys and QuickBook feature' },
];

const RBACManagement = () => {
  const { user } = useAuth();
  const [rbacStatus, setRbacStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // User editing state
  const [editingUser, setEditingUser] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Role editing state
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: [] });
  const [savingRole, setSavingRole] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Load RBAC status
      const statusResponse = await axios.get(`${apiBase}/rbac/status`, { headers });
      setRbacStatus(statusResponse.data);

      if (statusResponse.data.rbac_enabled) {
        // Load users, permissions, and roles
        const [usersResponse, permissionsResponse, rolesResponse] = await Promise.all([
          axios.get(`${apiBase}/admin/users`, { headers }),
          axios.get(`${apiBase}/rbac/permissions`, { headers }),
          axios.get(`${apiBase}/rbac/roles`, { headers })
        ]);

        setUsers(usersResponse.data || []);
        setPermissions(permissionsResponse.data.permissions || []);
        setRoles(rolesResponse.data.roles || []);
      }
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.post(`${apiBase}/rbac/migrate`, {}, { headers });
      setSuccess(response.data.message);
      loadData();
    } catch (err) {
      setError('Failed to migrate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getUserPermissionRecord = (userId) => {
    return permissions.find(p => p.user_id === userId);
  };

  const getUserDirectPermissions = (userId) => {
    const userPerms = getUserPermissionRecord(userId);
    return userPerms?.permissions || [];
  };

  const getUserRoleIds = (userId) => {
    const userPerms = getUserPermissionRecord(userId);
    return userPerms?.role_ids || [];
  };

  const getUserRoles = (userId) => {
    const roleIds = getUserRoleIds(userId);
    return roles.filter(r => roleIds.includes(r.id));
  };

  const getEffectivePermissions = (userId) => {
    const directPerms = new Set(getUserDirectPermissions(userId));
    const userRoles = getUserRoles(userId);
    userRoles.forEach(role => {
      (role.permissions || []).forEach(p => directPerms.add(p));
    });
    return Array.from(directPerms);
  };

  // User editing functions
  const openEditUserModal = (targetUser) => {
    setEditingUser(targetUser);
    setSelectedPermissions(getUserDirectPermissions(targetUser.id));
    setSelectedRoleIds(getUserRoleIds(targetUser.id));
    setError('');
    setSuccess('');
  };

  const closeEditUserModal = () => {
    setEditingUser(null);
    setSelectedPermissions([]);
    setSelectedRoleIds([]);
  };

  const togglePermission = (permission) => {
    if (permission === 'org_admin') {
      if (selectedPermissions.includes('org_admin')) {
        setSelectedPermissions([]);
      } else {
        setSelectedPermissions(['org_admin']);
      }
    } else {
      if (selectedPermissions.includes(permission)) {
        setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
      } else {
        setSelectedPermissions([
          ...selectedPermissions.filter(p => p !== 'org_admin'),
          permission
        ]);
      }
    }
  };

  const toggleRoleAssignment = (roleId) => {
    if (selectedRoleIds.includes(roleId)) {
      setSelectedRoleIds(selectedRoleIds.filter(id => id !== roleId));
    } else {
      setSelectedRoleIds([...selectedRoleIds, roleId]);
    }
  };

  const saveUserPermissions = async () => {
    if (!editingUser) return;

    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Save direct permissions
      await axios.put(
        `${apiBase}/rbac/permissions/${editingUser.id}`,
        { permissions: selectedPermissions },
        { headers }
      );

      // Save role assignments
      await axios.put(
        `${apiBase}/rbac/permissions/${editingUser.id}/roles`,
        { role_ids: selectedRoleIds },
        { headers }
      );

      setSuccess(`Permissions updated for ${editingUser.first_name} ${editingUser.last_name}`);
      closeEditUserModal();
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = err.message;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
      } else if (detail) {
        errorMsg = JSON.stringify(detail);
      }
      setError('Failed to save: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const removeAllUserPermissions = async (targetUser) => {
    if (!window.confirm(`Remove all RBAC permissions and roles from ${targetUser.first_name} ${targetUser.last_name}?`)) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.delete(`${apiBase}/rbac/permissions/${targetUser.id}`, { headers });
      setSuccess(`Permissions removed for ${targetUser.first_name} ${targetUser.last_name}`);
      loadData();
    } catch (err) {
      setError('Failed to remove permissions: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Role CRUD functions
  const openCreateRoleModal = () => {
    setRoleForm({ name: '', description: '', permissions: [] });
    setShowCreateRole(true);
    setEditingRole(null);
    setError('');
  };

  const openEditRoleModal = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowCreateRole(false);
    setError('');
  };

  const closeRoleModal = () => {
    setEditingRole(null);
    setShowCreateRole(false);
    setRoleForm({ name: '', description: '', permissions: [] });
  };

  const toggleRolePermission = (permission) => {
    const perms = roleForm.permissions;
    if (perms.includes(permission)) {
      setRoleForm({ ...roleForm, permissions: perms.filter(p => p !== permission) });
    } else {
      setRoleForm({ ...roleForm, permissions: [...perms, permission] });
    }
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) {
      setError('Role name is required');
      return;
    }

    setSavingRole(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      if (editingRole) {
        // Update existing role
        await axios.put(
          `${apiBase}/rbac/roles/${editingRole.id}`,
          {
            name: roleForm.name,
            description: roleForm.description || null,
            permissions: roleForm.permissions
          },
          { headers }
        );
        setSuccess(`Role "${roleForm.name}" updated`);
      } else {
        // Create new role
        await axios.post(
          `${apiBase}/rbac/roles`,
          {
            name: roleForm.name,
            description: roleForm.description || null,
            permissions: roleForm.permissions
          },
          { headers }
        );
        setSuccess(`Role "${roleForm.name}" created`);
      }

      closeRoleModal();
      loadData();
    } catch (err) {
      setError('Failed to save role: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingRole(false);
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.delete(`${apiBase}/rbac/roles/${role.id}`, { headers });
      setSuccess(`Role "${role.name}" deleted`);
      loadData();
    } catch (err) {
      setError('Failed to delete role: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-violet-solid"></div>
      </div>
    );
  }

  if (!rbacStatus?.rbac_enabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-ui-ink">RBAC Permissions</h1>
            <p className="text-ui-muted">Role-Based Access Control for your organization</p>
          </div>
        </div>

        <div className="bg-ui-amber-bg border border-ui-amber-line rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-ui-amber-solid mx-auto mb-4" />
          <h3 className="text-lg font-medium text-ui-ink mb-2">RBAC Not Enabled</h3>
          <p className="text-ui-ink-2">
            Role-Based Access Control is not enabled for your organization's package.
            Contact your administrator to enable this feature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ui-ink">RBAC Permissions</h1>
          <p className="text-ui-muted">Manage role-based access control for users</p>
        </div>
        <button
          onClick={handleMigrate}
          className="bg-ui-violet-bg hover:bg-ui-violet-line text-ui-violet-fg px-4 py-2 rounded-md font-medium text-sm"
        >
          Migrate Org Admins
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-4 flex items-start gap-3">
          <X className="w-5 h-5 text-ui-red-solid flex-shrink-0 mt-0.5" />
          <div className="text-ui-red-fg">{error}</div>
          <button onClick={() => setError('')} className="ml-auto text-ui-red-solid hover:text-ui-red-fg">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-ui-green-bg border border-ui-green-line rounded-md p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-ui-green-solid flex-shrink-0 mt-0.5" />
          <div className="text-ui-green-fg">{success}</div>
          <button onClick={() => setSuccess('')} className="ml-auto text-ui-green-solid hover:text-ui-green-fg">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-ui-line">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-ui-violet-solid text-ui-violet-fg'
                : 'border-transparent text-ui-muted hover:text-ui-ink-2 hover:border-ui-control'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            User Permissions
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roles'
                ? 'border-ui-violet-solid text-ui-violet-fg'
                : 'border-transparent text-ui-muted hover:text-ui-ink-2 hover:border-ui-control'
            }`}
          >
            <Tag className="w-4 h-4 inline mr-2" />
            Roles
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Info box */}
          <div className="bg-ui-blue-bg border border-ui-blue-line rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-ui-blue-solid flex-shrink-0 mt-0.5" />
            <div className="text-sm text-ui-blue-fg">
              Assign <strong>roles</strong> for grouped permissions, or grant <strong>direct permissions</strong> for specific access.
              Organization Admin grants access to all features.
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-ui-surface border border-ui-line rounded-lg shadow-ui-card overflow-hidden">
            <div className="px-6 py-4 border-b border-ui-divider">
              <h2 className="text-lg font-semibold text-ui-ink">User Permissions</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ui-divider">
                <thead className="bg-ui-sunk">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider">Roles</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ui-muted uppercase tracking-wider">Direct Permissions</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-ui-muted uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-ui-surface divide-y divide-ui-divider">
                  {users.map((orgUser) => {
                    const userRoles = getUserRoles(orgUser.id);
                    const directPerms = getUserDirectPermissions(orgUser.id);
                    const isOrgAdmin = directPerms.includes('org_admin') || orgUser.is_organization_admin;

                    return (
                      <tr key={orgUser.id} className="hover:bg-ui-hover">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-ui-ink">
                              {orgUser.first_name} {orgUser.last_name}
                              {orgUser.id === user.id && (
                                <span className="ml-2 text-xs text-ui-muted">(you)</span>
                              )}
                            </div>
                            <div className="text-sm text-ui-muted">{orgUser.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {userRoles.length > 0 ? (
                              userRoles.map(role => (
                                <span
                                  key={role.id}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ui-green-bg text-ui-green-fg"
                                  title={role.description || role.name}
                                >
                                  <Tag className="w-3 h-3 mr-1" />
                                  {role.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-ui-faint">No roles</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {isOrgAdmin ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ui-violet-bg text-ui-violet-fg">
                                <Shield className="w-3 h-3 mr-1" />
                                Organization Admin
                              </span>
                            ) : directPerms.length > 0 ? (
                              directPerms.map(perm => (
                                <span
                                  key={perm}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ui-blue-bg text-ui-blue-fg"
                                >
                                  {RBAC_PERMISSIONS.find(p => p.value === perm)?.label || perm}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-ui-faint">None</span>
                            )}
                            {orgUser.is_organization_admin && !directPerms.includes('org_admin') && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ui-hover text-ui-ink-3">
                                Legacy Admin
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {orgUser.role !== 'superadmin' && (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openEditUserModal(orgUser)}
                                className="text-ui-link hover:text-ui-link/80 text-sm font-medium"
                              >
                                Edit
                              </button>
                              {(directPerms.length > 0 || userRoles.length > 0) && orgUser.id !== user.id && (
                                <button
                                  onClick={() => removeAllUserPermissions(orgUser)}
                                  className="text-ui-red-fg hover:text-ui-red-fg/80 text-sm font-medium"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          )}
                          {orgUser.role === 'superadmin' && (
                            <span className="text-xs text-ui-muted">Superadmin</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <>
          {/* Info box */}
          <div className="bg-ui-blue-bg border border-ui-blue-line rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-ui-blue-solid flex-shrink-0 mt-0.5" />
            <div className="text-sm text-ui-blue-fg">
              Create roles to group permissions together. Assign roles to users for easier permission management.
            </div>
          </div>

          {/* Roles List */}
          <div className="bg-ui-surface border border-ui-line rounded-lg shadow-ui-card overflow-hidden">
            <div className="px-6 py-4 border-b border-ui-divider flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ui-ink">Roles</h2>
              <button
                onClick={openCreateRoleModal}
                className="inline-flex items-center px-3 py-2 bg-ui-violet-strong text-ui-on-primary rounded-md hover:bg-ui-violet-strong/90 text-sm font-medium"
              >
                <Plus className="w-4 h-4 mr-1" />
                Create Role
              </button>
            </div>

            {roles.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Tag className="w-12 h-12 text-ui-dash mx-auto mb-4" />
                <p className="text-ui-muted">No roles defined yet.</p>
                <p className="text-sm text-ui-faint mt-1">Create a role to group permissions together.</p>
              </div>
            ) : (
              <div className="divide-y divide-ui-divider">
                {roles.map(role => {
                  const roleUsers = users.filter(u => getUserRoleIds(u.id).includes(role.id));
                  return (
                    <div key={role.id} className="px-6 py-4 hover:bg-ui-hover">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-ui-ink">{role.name}</h3>
                            {role.is_system_role && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ui-hover text-ui-ink-3">
                                System
                              </span>
                            )}
                          </div>
                          {role.description && (
                            <p className="text-sm text-ui-muted mt-1">{role.description}</p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(role.permissions || []).map(perm => (
                              <span
                                key={perm}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ui-blue-bg text-ui-blue-fg"
                              >
                                {RBAC_PERMISSIONS.find(p => p.value === perm)?.label || perm}
                              </span>
                            ))}
                            {(!role.permissions || role.permissions.length === 0) && (
                              <span className="text-xs text-ui-faint">No permissions</span>
                            )}
                          </div>
                          {roleUsers.length > 0 && (
                            <p className="text-xs text-ui-muted mt-2">
                              Assigned to {roleUsers.length} user{roleUsers.length !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => openEditRoleModal(role)}
                            className="p-1.5 text-ui-muted hover:text-ui-link rounded"
                            title="Edit role"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteRole(role)}
                            className="p-1.5 text-ui-muted hover:text-ui-red-fg rounded"
                            title="Delete role"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit User Permissions Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-ui-overlay/50 flex items-center justify-center z-[100] p-4">
          <div className="rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col bg-ui-raised">
            <div className="px-6 py-4 border-b flex-shrink-0 border-ui-line">
              <h3 className="text-lg font-semibold text-ui-ink">
                Edit Permissions
              </h3>
              <p className="text-sm text-ui-muted">
                {editingUser.first_name} {editingUser.last_name}
              </p>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
              {/* Role Assignment Section */}
              {roles.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3 flex items-center text-ui-ink">
                    <Tag className="w-4 h-4 mr-2 text-ui-green-fg" />
                    Assign Roles
                  </h4>
                  <div className="space-y-2">
                    {roles.map(role => (
                      <label
                        key={role.id}
                        className={`flex items-start p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                          selectedRoleIds.includes(role.id)
                            ? 'border-ui-green-solid bg-ui-green-bg'
                            : 'border-ui-control hover:border-ui-dash bg-ui-hover'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.id)}
                          onChange={() => toggleRoleAssignment(role.id)}
                          className="h-4 w-4 text-ui-green-solid border-ui-control rounded mt-0.5"
                        />
                        <div className="ml-3">
                          <span className="block text-sm font-medium text-ui-ink">{role.name}</span>
                          {role.description && (
                            <span className="text-xs text-ui-muted">{role.description}</span>
                          )}
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(role.permissions || []).slice(0, 3).map(perm => (
                              <span
                                key={perm}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-ui-control text-ui-ink-2"
                              >
                                {RBAC_PERMISSIONS.find(p => p.value === perm)?.label || perm}
                              </span>
                            ))}
                            {(role.permissions || []).length > 3 && (
                              <span className="text-xs text-ui-muted">
                                +{role.permissions.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Direct Permissions Section */}
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center text-ui-ink">
                  <Shield className="w-4 h-4 mr-2 text-ui-violet-fg" />
                  Direct Permissions
                </h4>
                <div className="space-y-2">
                  {RBAC_PERMISSIONS.map((perm) => (
                    <label
                      key={perm.value}
                      className={`flex items-start p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                        selectedPermissions.includes(perm.value)
                          ? 'border-ui-violet-solid bg-ui-violet-bg'
                          : 'border-ui-control hover:border-ui-dash bg-ui-hover'
                      } ${
                        perm.value !== 'org_admin' && selectedPermissions.includes('org_admin')
                          ? 'opacity-50'
                          : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(perm.value) ||
                          (perm.value !== 'org_admin' && selectedPermissions.includes('org_admin'))}
                        onChange={() => togglePermission(perm.value)}
                        disabled={perm.value !== 'org_admin' && selectedPermissions.includes('org_admin')}
                        className="h-4 w-4 text-ui-violet-solid border-ui-control rounded mt-0.5"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-ui-ink">{perm.label}</span>
                        <span className="text-xs text-ui-muted">{perm.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="px-6 py-2 border-t flex-shrink-0 bg-ui-red-bg border-ui-red-line">
                <div className="text-sm text-ui-red-fg">{error}</div>
              </div>
            )}

            <div className="px-6 py-4 border-t flex justify-end gap-3 flex-shrink-0 border-ui-line bg-ui-raised">
              <button
                onClick={closeEditUserModal}
                className="px-4 py-2 border rounded-md text-ui-ink-2 bg-ui-hover border-ui-control hover:bg-ui-control"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={saveUserPermissions}
                className="px-4 py-2 bg-ui-violet-strong text-ui-on-primary rounded-md hover:bg-ui-violet-strong/90 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Role Modal */}
      {(showCreateRole || editingRole) && (
        <div className="fixed inset-0 bg-ui-overlay/50 flex items-center justify-center z-[100] p-4">
          <div className="rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col bg-ui-raised">
            <div className="px-6 py-4 border-b flex-shrink-0 border-ui-line">
              <h3 className="text-lg font-semibold text-ui-ink">
                {editingRole ? 'Edit Role' : 'Create Role'}
              </h3>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-ui-ink-2">
                  Role Name *
                </label>
                <input
                  type="text"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ui-violet-solid bg-ui-sunk border-ui-control text-ui-ink placeholder:text-ui-faint"
                  placeholder="e.g., Billing Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-ui-ink-2">
                  Description
                </label>
                <textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ui-violet-solid bg-ui-sunk border-ui-control text-ui-ink placeholder:text-ui-faint"
                  rows={2}
                  placeholder="Describe what this role is for..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-ui-ink-2">
                  Permissions
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {RBAC_PERMISSIONS.filter(p => p.value !== 'org_admin').map((perm) => (
                    <label
                      key={perm.value}
                      className={`flex items-start p-2 rounded cursor-pointer border transition-colors ${
                        roleForm.permissions.includes(perm.value)
                          ? 'border-ui-violet-solid bg-ui-violet-bg'
                          : 'border-ui-control hover:border-ui-dash bg-ui-hover'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={roleForm.permissions.includes(perm.value)}
                        onChange={() => toggleRolePermission(perm.value)}
                        className="h-4 w-4 text-ui-violet-solid border-ui-control rounded mt-0.5"
                      />
                      <div className="ml-2">
                        <span className="block text-sm font-medium text-ui-ink">{perm.label}</span>
                        <span className="text-xs text-ui-muted">{perm.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="px-6 py-2 border-t flex-shrink-0 bg-ui-red-bg border-ui-red-line">
                <div className="text-sm text-ui-red-fg">{error}</div>
              </div>
            )}

            <div className="px-6 py-4 border-t flex justify-end gap-3 flex-shrink-0 border-ui-line bg-ui-raised">
              <button
                onClick={closeRoleModal}
                className="px-4 py-2 border rounded-md text-ui-ink-2 bg-ui-hover border-ui-control hover:bg-ui-control"
                disabled={savingRole}
              >
                Cancel
              </button>
              <button
                onClick={saveRole}
                className="px-4 py-2 bg-ui-violet-strong text-ui-on-primary rounded-md hover:bg-ui-violet-strong/90 disabled:opacity-50"
                disabled={savingRole}
              >
                {savingRole ? 'Saving...' : (editingRole ? 'Update Role' : 'Create Role')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RBACManagement;
